import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '@/components/Modal/modal';
import EmailLink from '@/components/EmailLink';
import Button from '@/components/Button';
import {
  SharepointHealthResponse,
  StatusSummaryResponse,
  SyncRunModel,
  IngestStatus,
  FolderTreeResponse,
} from '@/models/sharepoint';
import RunDetailsTab from './RunDetailsTab';
import styles from './sharepoint-modal.module.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  folders: FolderTreeResponse;
  topic: 'instructions' | 'settings';
  summary?: StatusSummaryResponse;
  syncRuns?: SyncRunModel[];
  health?: SharepointHealthResponse;
  isLoading?: boolean;
  onSync?: () => void;
  isSyncing?: boolean;
};

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

const SharepointModal = ({
  isOpen,
  onClose,
  topic,
  summary,
  folders,
  syncRuns,
  health,
  isLoading,
  onSync,
  isSyncing,
}: Props) => {
  const isHealthy = health?.is_healthy ?? true;
  const [activeTab, setActiveTab] = useState<'overview' | 'runs'>('overview');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const navigateToRun = (runId: string) => {
    setSelectedRunId(runId);
    setActiveTab('runs');
  };

  return createPortal(
    <Modal
      title={
        topic === 'instructions'
          ? 'Connect to SharePoint'
          : 'SharePoint Settings'
      }
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
    >
      {topic === 'instructions' ? (
        <div>
          <p>
            To connect SharePoint to your Workspace, please send an email to{' '}
            <EmailLink /> with the following information:
          </p>
          <ul>
            <li>The email address associated with your SharePoint account.</li>
            <li>The email address of your account in our Workspace.</li>
            <li>The URL of your SharePoint site or tenant.</li>
            <li>
              The type of data you intend to sync (e.g., documents,
              spreadsheets, etc).
            </li>
            <li>
              A rough estimate of the number of files and total size you plan to
              sync from SharePoint.
            </li>
          </ul>
          <p>
            Our team will reach out to you with the next steps to complete the
            connection process.
          </p>
        </div>
      ) : (
        <div className={styles['sharepoint-settings']}>
          <div className={styles['tab-nav']}>
            <button
              className={styles['tab-btn']}
              data-active={String(activeTab === 'overview')}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={styles['tab-btn']}
              data-active={String(activeTab === 'runs')}
              onClick={() => setActiveTab('runs')}
            >
              Run Details
            </button>
          </div>

          {activeTab === 'overview' && (
            <div key="overview" className={styles['tab-content']}>
              <p
                className={styles['health-status']}
                data-healthy={String(isHealthy)}
              >
                {health
                  ? `SharePoint is ${health.is_healthy ? 'connected and healthy' : 'connected but unhealthy'}${!health.is_healthy && health.message ? ` · ${health.message}` : ''}.`
                  : 'SharePoint is currently connected.'}
              </p>

              {folders?.root_folders?.length > 0 && (
                <div className={styles['synced-folders']}>
                  <span className={styles['synced-folders__label']}>
                    Synced folders
                  </span>
                  <div className={styles['synced-folders__chips']}>
                    {folders.root_folders.map((folder) => (
                      <span key={folder.path} className={styles['folder-chip']}>
                        {folder.path}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles['stats-grid']}>
                <div className={styles['stat-card']}>
                  <span className={styles['stat-label']}>Last Synced</span>
                  <span
                    className={styles['stat-value']}
                    style={{ fontSize: 14 }}
                  >
                    {summary?.last_sync
                      ? new Date(summary.last_sync).toLocaleDateString()
                      : '—'}
                  </span>
                </div>
                <div className={styles['stat-card']}>
                  <span className={styles['stat-label']}>Total Files</span>
                  <span className={styles['stat-value']}>
                    {summary?.total_files ?? 0}
                  </span>
                </div>
                <div className={styles['stat-card']}>
                  <span className={styles['stat-label']}>Processing</span>
                  <span className={styles['stat-value']}>
                    {summary?.by_ingest_status?.[IngestStatus.IN_PROGRESS] ?? 0}
                  </span>
                </div>
                <div className={styles['stat-card']}>
                  <span className={styles['stat-label']}>Last 24h</span>
                  <span className={styles['stat-value']}>
                    {summary?.files_last_24h ?? 0}
                  </span>
                </div>
              </div>

              <div className={styles['sync-runs-section']}>
                <h4>Recent Sync Runs</h4>
                <div className={styles['table-wrapper']}>
                  {isLoading ? (
                    <p className={styles['empty-state']}>
                      Loading sync runs...
                    </p>
                  ) : syncRuns && syncRuns.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Date & Time</th>
                          <th className={styles['col-status']}>Status</th>
                          <th>Seen</th>
                          <th>Skipped</th>
                          <th>Errors</th>
                          <th>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {syncRuns.slice(0, 10).map((run) => (
                          <tr key={run.mongodb_document_id}>
                            <td
                              className={styles['clickable-cell']}
                              onClick={() =>
                                navigateToRun(run.mongodb_document_id)
                              }
                            >
                              {new Date(run.timestamp).toLocaleString(
                                undefined,
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                }
                              )}
                            </td>
                            <td className={styles['col-status']}>
                              <span
                                className={styles['status-badge']}
                                data-status={run.status}
                              >
                                {run.status}
                              </span>
                            </td>
                            <td>{run.files_seen}</td>
                            <td>{run.files_skipped}</td>
                            <td
                              className={
                                run.errors > 0
                                  ? styles['clickable-cell']
                                  : undefined
                              }
                              onClick={
                                run.errors > 0
                                  ? () => navigateToRun(run.mongodb_document_id)
                                  : undefined
                              }
                            >
                              {run.errors}
                            </td>
                            <td>{formatDuration(run.duration_seconds)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className={styles['empty-state']}>
                      No recent sync runs available.
                    </p>
                  )}
                </div>
              </div>

              <div className={styles['actions']}>
                <Button
                  onClick={onSync}
                  disabled={isSyncing}
                  loading={isSyncing}
                  loadingText="Syncing..."
                >
                  Re-sync
                </Button>
              </div>

              <p className={styles['disconnect-note']}>
                If you wish to disconnect SharePoint, please contact{' '}
                <EmailLink /> and our team will assist you.
              </p>
            </div>
          )}

          {activeTab === 'runs' && (
            <div key="runs" className={styles['tab-content']}>
              <RunDetailsTab
                syncRuns={syncRuns ?? []}
                initialRunId={selectedRunId}
              />
            </div>
          )}
        </div>
      )}
    </Modal>,
    document.body
  );
};

export default SharepointModal;
